import { PawPrint, ShoppingBag, Menu, X, Search, PackageSearch } from "lucide-react";
import { useCart } from "../lib/CartContext";
import { useState } from "react";
import { useLang } from "../lib/i18n";
import LangToggle from "./LangToggle";
import SearchModal from "./SearchModal";
import OrderTrackingModal from "./OrderTrackingModal";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);

  const LINKS = [
    { id: "categories", label: t("nav.categories") },
    { id: "products", label: t("nav.shop") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{ background: "rgba(255,251,242,0.85)", borderBottom: "2px solid #0F172A" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="brand-logo" aria-label="Pet Munchies">
          <img src="/logo.png" alt="Pet Munchies" className="h-10 sm:h-11 w-auto object-contain" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium hover:text-orange-500 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle />
          <button
            data-testid="nav-search-btn"
            onClick={() => setSearchOpen(true)}
            className="neo-btn bg-white p-2"
            aria-label={t("search.title")}
          >
            <Search size={16} />
          </button>
          <button
            data-testid="nav-track-order-btn"
            onClick={() => setTrackingOpen(true)}
            className="hidden sm:inline-flex neo-btn bg-white p-2"
            aria-label={t("tracking.nav")}
          >
            <PackageSearch size={16} />
          </button>
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="neo-btn bg-white px-3 py-2 flex items-center gap-2 text-sm"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline">{t("nav.cart")}</span>
            <span
              className="ms-1 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full text-xs font-bold text-white px-1"
              style={{ background: "var(--pm-dog)" }}
              data-testid="cart-count"
            >
              {count}
            </span>
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden neo-btn bg-white p-2"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t-2 border-slate-900 bg-white">
          <div className="px-4 py-3 flex flex-col gap-2">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-start py-2 font-medium"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => {
                setMobileOpen(false);
                setTrackingOpen(true);
              }}
              className="text-start py-2 font-medium"
              data-testid="mobile-nav-track-order"
            >
              {t("tracking.nav")}
            </button>
          </div>
        </div>
      )}

      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
      <OrderTrackingModal open={trackingOpen} onOpenChange={setTrackingOpen} />
    </header>
  );
}
